"use client";

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { createClient, type SupabaseClient } from "@supabase/supabase-js";
import type { LucideIcon } from "lucide-react";
import {
  BookOpenCheck,
  AppWindow,
  ArrowLeftRight,
  Check,
  ChevronDown,
  ChevronRight,
  CircleAlert,
  Clapperboard,
  Database,
  FileSearch,
  Film,
  Gauge,
  LayoutGrid,
  LoaderCircle,
  LogIn,
  LogOut,
  Mail,
  Moon,
  MoreVertical,
  Plus,
  RefreshCw,
  Search,
  Settings2,
  ServerCog,
  ShieldCheck,
  Sparkles,
  Sun,
  UserPlus,
  Users,
  Workflow,
  Waypoints,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Progress } from "@/components/ui/progress";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { ManualChatGPTDesk } from "./manual-chatgpt-desk";
import { DatabaseBridge } from "./database-bridge";
import { AiWorkspace } from "./ai-workspace";
import { BackendExplorer } from "./backend-explorer";
import { UniversalFileExplorer } from "./universal-file-explorer";
import { PluginConnectionsPanel } from "./plugin-connections-panel";
import { MemberProjectPanel } from "./member-project-panel";
import { MemberDataAuthPanel, type MemberProjectRuntimeProfile } from "./member-data-auth-panel";
import { MemberManagementPanel } from "./member-management-panel";
import { M4ControlTowerPanel } from "./m4-control-tower-panel";
import { AccountSetupPanel } from "./account-setup-panel";
import { SettingsPanel, StatusCard } from "./settings-panel";
import { EnterpriseInfrastructureWorkspace } from "./enterprise-infrastructure-workspace";
import { EnterpriseBottomNav, EnterpriseHeader, EnterpriseHome, EnterpriseMoreWorkspace, EnterpriseSidebar, type EnterpriseRoute } from "./enterprise-ui-shell";
import { enterpriseWorkspace } from "./enterprise-workspace-registry";
import { RunyiUiRuntimeCoordinator } from "./runyi-ui-coordinator";

type Tab = EnterpriseRoute;
type MovieTab = "create" | "ai" | "bridge" | "workflow" | "catalogue" | "backend" | "settings";

function runyiScreenKeyForRoute(route: EnterpriseRoute): string {
  if (route === "infrastructure" || route === "catalogue" || route === "bridge" || route === "flow" || route === "operations") return "MARKETPLACE";
  if (route === "new") return "NEW_INFRASTRUCTURE";
  if (route === "applications" || route === "movies") return "APPS";
  if (route === "files") return "EXPLORER";
  if (route === "governance") return "SETTINGS";
  if (route === "ai") return "AI_USAGE";
  return "ROOT";
}
type Appearance = "light" | "dark";
type Health = {
  configured: boolean;
  connected: boolean;
  signedIn?: boolean;
  movieCount?: number;
  conceptRunCount?: number;
  schemaVersion?: string;
  privateAccount?: boolean;
  role?: "OWNER_ADMIN" | "ADMIN" | "MEMBER";
  activeUserCount?: number;
  maximumUsers?: number;
  error?: string;
};

type AllowedUser = {
  email: string;
  role: "OWNER_ADMIN" | "ADMIN" | "MEMBER";
  active: boolean;
  user_id?: string | null;
  permissions?: Record<string, boolean>;
  accessible_user_ids?: string[];
  joined: boolean;
  created_at?: string | null;
  activated_at?: string | null;
  last_seen_at?: string | null;
};

type AndroidAuthHandoff = {
  accessToken: string;
  refreshToken: string;
  expiresIn: string;
};

type PendingAndroidSession = {
  accessToken: string;
  refreshToken: string;
};

const SUPABASE_URL =
  import.meta.env.VITE_SUPABASE_URL ||
  "https://enbisficlrovjuqxjksy.supabase.co";
const EMBEDDED_PUBLISHABLE_KEY = "sb_publishable_uZhg8SFREduI9flAbRtzqA_kEi1X4Ws";
const DEFAULT_PUBLISHABLE_KEY =
  import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || EMBEDDED_PUBLISHABLE_KEY;
const PENDING_ANDROID_SESSION_STORAGE = "runyi:pending-android-session";
const APPEARANCE_STORAGE = "runyi:appearance";

function isSecretKey(key: string): boolean {
  if (key.startsWith("sb_secret_") || /service[_-]?role/i.test(key)) return true;
  try {
    const payload = key.split(".")[1];
    if (!payload) return false;
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(payload.length / 4) * 4, "=");
    return JSON.parse(atob(normalized)).role === "service_role";
  } catch {
    return false;
  }
}

function makeSupabaseClient(key: string): SupabaseClient | null {
  const cleanKey = key.trim();
  if (!cleanKey || isSecretKey(cleanKey)) return null;
  try {
    return createClient(SUPABASE_URL, cleanKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
        storageKey: "runyi:supabase-auth",
      },
    });
  } catch {
    return null;
  }
}
type Intake = {
  theme: string;
  route: string;
  sourceForm: string;
  transformationDistance: string;
  primaryThemeLane: string;
  secondaryThemeLane: string;
  emotionalGenre: string;
  pressureArena: string;
  humanQuestion: string;
  storyStatus: string;
  platform: string;
  aspectRatio: string;
  productionProfile: string;
  pacing: string;
  duration: string;
};

const initialIntake: Intake = {
  theme: "",
  route: "O",
  sourceForm: "SOURCE_FREE",
  transformationDistance: "NONE",
  primaryThemeLane: "O1",
  secondaryThemeLane: "NONE",
  emotionalGenre: "FAMILY",
  pressureArena: "FAMILY_BUSINESS",
  humanQuestion: "",
  storyStatus: "STANDALONE",
  platform: "YouTube",
  aspectRatio: "16:9",
  productionProfile: "STANDARD",
  pacing: "B",
  duration: "20",
};

const movieNavItems = [
  { id: "ai" as const, label: "AI Workspace", icon: Sparkles },
  { id: "create" as const, label: "New Movie", icon: Plus },
  { id: "bridge" as const, label: "DB Bridge", icon: ArrowLeftRight },
  { id: "workflow" as const, label: "Workflow", icon: Workflow },
  { id: "catalogue" as const, label: "Library", icon: LayoutGrid },
  { id: "backend" as const, label: "Backend", icon: Database },
  { id: "settings" as const, label: "Settings", icon: Settings2 },
];

const creativeChecks = [
  "Distinct premise and protagonist contradiction",
  "Specific story world and cultural observation",
  "Non-interchangeable climax and ending family",
  "Every scene changes knowledge, choice, or stakes",
  "Clear creative, educational, or human value",
];

const themeLanes = [
  ["O1", "O1 · Cross-Class Protection"],
  ["O2", "O2 · Asymmetric Devotion"],
  ["O3", "O3 · Betrayal of Blind Trust"],
  ["O4", "O4 · Unwavering Loyalty Under Mockery"],
];

const emotionalGenres = [
  ["FAMILY", "Family"],
  ["RELATIONSHIP", "Relationship / romantic"],
  ["FRIENDSHIP", "Friendship"],
  ["COMMUNITY", "Community"],
  ["WORKPLACE", "Workplace"],
  ["SOCIAL_JUSTICE", "Social justice"],
  ["COMING_OF_AGE", "Coming of age"],
  ["INHERITANCE", "Inheritance"],
];

const pressureArenas = [
  ["FAMILY_BUSINESS", "Family business"],
  ["CORPORATE", "Corporate"],
  ["PROPERTY", "Property"],
  ["EDUCATION", "Education"],
  ["LAW_INHERITANCE", "Law / inheritance"],
  ["HEALTHCARE", "Healthcare"],
  ["CHURCH_COMMUNITY", "Church / community"],
  ["CREATIVE_INDUSTRY", "Creative industry"],
  ["HOSPITALITY", "Hospitality"],
  ["DOMESTIC_EMPLOYMENT", "Domestic employment"],
  ["CONSTRUCTION", "Construction"],
  ["TRANSPORT", "Transport"],
  ["AGRICULTURE", "Agriculture"],
  ["ENTERTAINMENT", "Entertainment"],
];

const workflowPhases = [
  {
    phase: "Entry and route control",
    canonical: [
      ["1", "Receive input", "1 · Receive source or source-free creative brief"],
      ["2", "Confirm route", "2 · Confirm route and source form"],
      ["3", "Select pacing and runtime", "3 · Select pacing and runtime"],
      ["4", "Catalogue comparison context", "4 · Retrieve catalogue context"],
    ],
  },
  {
    phase: "Concept and catalogue control",
    canonical: [
      ["5T / 5O", "Route and premise control · STOP", "5–21 · Open RAFC record; establish route authority; generate 12; register candidates; Story DNA; latest-10 comparison; 3–10 full-catalogue matches; trope and combination audits; mutate; editorial fingerprint; viewer test; 15-dimension score; checkpoints; shortlist; approve one authority", "22 · Record RAF promotion eligibility only—no RAF assigned"],
    ],
  },
  {
    phase: "Story design",
    canonical: [
      ["6", "Story Bible", "23 · Create pre-screenplay Story Bible"],
      ["7", "World and Prop Bible", "24 · Create World and Prop Bible"],
      ["8", "Causality and story systems", "25 · Build causality and story systems"],
      ["9", "Route-specific blueprint", "26 · Complete route-specific blueprint"],
    ],
  },
  {
    phase: "Retention and drama architecture",
    canonical: [
      ["10", "Define emotional and click promise", "27 · Define emotional and click promise"],
      ["11", "Canonical Retention Blueprint", "28 · Build the single canonical Retention Blueprint"],
      ["12", "Drama-First architecture", "29 · Build premise-earned Drama-First act architecture"],
      ["13", "Scene-by-scene outline", "30 · Create scene-by-scene outline"],
      ["14", "Beat-by-beat structure", "31 · Create beat-by-beat structure"],
    ],
  },
  {
    phase: "Validation and approval",
    canonical: [
      ["15", "Route fidelity", "32 · Complete route-specific and story audits"],
      ["16", "Channel originality audit", "32 · Complete route-specific and story audits"],
      ["17", "Story and feasibility audits", "32 · Complete route-specific and story audits"],
      ["18", "Pre-screenplay originality gate", "33 · Run mandatory pre-screenplay originality gate"],
      ["19", "Save blueprint TXT", "33 · Preserve approved blueprint evidence"],
      ["20", "Explicit blueprint approval", "33 · Human approval is mandatory"],
      ["21", "Handoff approved blueprint", "34 · Prepare approved screenplay-writer handoff"],
    ],
  },
  {
    phase: "Screenplay, production and closeout",
    canonical: [
      ["22", "Approved model creates dialogue and shots", "34 · Execute approved-model handoff; assign RAF only now"],
      ["23", "Audit screenplay", "35 · Final screenplay catalogue check and enter production"],
      ["24", "Production, packaging and publication", "35 · Enter production", "36 · Publication, disclosure and database closeout"],
    ],
  },
];

export function MovieEngineDashboard() {
  const [tab, setTab] = useState<Tab>("infrastructure");
  const [movieTab, setMovieTab] = useState<MovieTab>("ai");
  const [appearance, setAppearance] = useState<Appearance>("dark");
  const [health, setHealth] = useState<Health>({ configured: false, connected: false });
  const [healthLoading, setHealthLoading] = useState(true);
  const [intake, setIntake] = useState<Intake>(initialIntake);
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [createdRun, setCreatedRun] = useState<string | null>(null);
  const publishableKey = DEFAULT_PUBLISHABLE_KEY;
  const [signedInEmail, setSignedInEmail] = useState<string | null>(null);
  const [memberProjectProfile, setMemberProjectProfile] = useState<MemberProjectRuntimeProfile | null>(null);
  const [tenantSignedInEmail, setTenantSignedInEmail] = useState<string | null>(null);
  const [tenantAccessToken, setTenantAccessToken] = useState<string | null>(null);
  const [authNotice, setAuthNotice] = useState<string | null>(null);
  const [authLoading, setAuthLoading] = useState(false);
  const [isAndroidAuthReturn, setIsAndroidAuthReturn] = useState(false);
  const [androidAuthHandoff, setAndroidAuthHandoff] = useState<AndroidAuthHandoff | null>(null);
  const [androidReturnNotice, setAndroidReturnNotice] = useState<string | null>(null);
  const androidAuthInProgress = useRef(false);
  const healthRefreshInFlight = useRef<Promise<boolean> | null>(null);

  const supabase = useMemo(() => makeSupabaseClient(publishableKey), [publishableKey]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("runyi_infrastructure_session")) {
      setTab("new");
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const openAddInfrastructure = (event: Event) => {
      const detail = (event as CustomEvent<Record<string,unknown>>).detail ?? {};
      const source = String(detail.source ?? "");
      const targetScope = String(detail.target_scope ?? "");
      const fromApplicationOrProject = source === "APPLICATION_MENU" || source === "PROJECT_MENU"
        || Boolean(detail.application_entity_key) || Boolean(detail.project_id);
      const fromInfrastructure = source === "INFRASTRUCTURE_ACTION_MENU"
        || Boolean(detail.target_entity_key) || Boolean(detail.provider_capability_key);

      const targetRoute: Tab = fromInfrastructure && (targetScope === "APPLICATION" || targetScope === "PROJECT")
        ? "applications"
        : fromApplicationOrProject
          ? "infrastructure"
          : "new";

      try {
        window.sessionStorage.setItem("runyi:add-infrastructure:intent",JSON.stringify(detail));
        window.sessionStorage.setItem("runyi:selection-context",JSON.stringify({
          ...detail,
          origin_route: tab,
          target_route: targetRoute,
          entered_target: false,
          started_at: new Date().toISOString(),
        }));
      } catch {
        // Session storage only preserves the scoped workflow across nested workspace navigation.
      }
      setTab(targetRoute);
    };
    window.addEventListener("runyi:add-infrastructure",openAddInfrastructure as EventListener);
    return () => window.removeEventListener("runyi:add-infrastructure",openAddInfrastructure as EventListener);
  }, [tab]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = window.sessionStorage.getItem("runyi:selection-context");
      if (!raw) return;
      const context = JSON.parse(raw) as { origin_route?: Tab; target_route?: Tab; entered_target?: boolean };
      if (context.target_route === tab && !context.entered_target) {
        window.sessionStorage.setItem("runyi:selection-context",JSON.stringify({...context,entered_target:true}));
        return;
      }
      if (context.origin_route === tab && context.entered_target) {
        window.sessionStorage.removeItem("runyi:selection-context");
        window.sessionStorage.removeItem("runyi:add-infrastructure:intent");
      }
    } catch {
      window.sessionStorage.removeItem("runyi:selection-context");
    }
  }, [tab]);

  const memberSupabase = useMemo(() => {
    if (
      !memberProjectProfile?.configured
      || !memberProjectProfile.project_url
      || !memberProjectProfile.publishable_key
      || memberProjectProfile.connection_status !== "VERIFIED"
    ) return null;
    try {
      return createClient(memberProjectProfile.project_url, memberProjectProfile.publishable_key, {
        auth: {
          persistSession: false,
          autoRefreshToken: false,
          detectSessionInUrl: false,
        },
      });
    } catch {
      return null;
    }
  }, [memberProjectProfile]);

  const memberDataSupabase = useMemo(() => {
    if (
      !tenantAccessToken
      || !memberProjectProfile?.configured
      || !memberProjectProfile.project_url
      || !memberProjectProfile.publishable_key
      || memberProjectProfile.connection_status !== "VERIFIED"
    ) return null;
    try {
      return createClient(memberProjectProfile.project_url, memberProjectProfile.publishable_key, {
        auth: {
          persistSession: false,
          autoRefreshToken: false,
          detectSessionInUrl: false,
        },
        global: {
          headers: {
            Authorization: `Bearer ${tenantAccessToken}`,
          },
        },
      });
    } catch {
      return null;
    }
  }, [memberProjectProfile, tenantAccessToken]);

  const memberDataPlaneInstalled = Boolean(
    memberProjectProfile?.connection_status === "VERIFIED"
    && (
      memberProjectProfile?.schema_version === "member-data-v2"
      || memberProjectProfile?.schema_version === "member-enterprise-v3"
      || memberProjectProfile?.schema_version === "member-control-tower-v4"
    ),
  );
  const dataSupabase = memberDataPlaneInstalled ? memberDataSupabase : supabase;
  const dataConnected = memberDataPlaneInstalled
    ? Boolean(memberDataSupabase && tenantSignedInEmail)
    : health.connected;

  const applyAppearance = useCallback((nextAppearance: Appearance, persist = true) => {
    setAppearance(nextAppearance);
    document.documentElement.dataset.theme = nextAppearance;
    document.documentElement.style.colorScheme = nextAppearance;
    const themeColor = nextAppearance === "light" ? "#ffffff" : "#000000";
    document.querySelector('meta[name="theme-color"]')?.setAttribute("content", themeColor);
    if (persist) window.localStorage.setItem(APPEARANCE_STORAGE, nextAppearance);

    const androidBridge = (window as Window & {
      RunyiAndroid?: { setTheme?: (theme: Appearance) => void };
    }).RunyiAndroid;
    androidBridge?.setTheme?.(nextAppearance);
  }, []);

  useEffect(() => {
    const saved = window.localStorage.getItem(APPEARANCE_STORAGE);
    applyAppearance(saved === "light" ? "light" : "dark", false);
  }, [applyAppearance]);

  const themeLength = intake.theme.trim().length;
  const readiness = useMemo(() => {
    let value = 25;
    if (themeLength >= 12) value += 35;
    if (intake.duration) value += 15;
    if (intake.storyStatus) value += 15;
    if (intake.pacing) value += 10;
    return Math.min(value, 100);
  }, [intake, themeLength]);


  const refreshHealth = useCallback(async () => {
    if (!supabase) {
      setSignedInEmail(null);
      setHealth({ configured: false, connected: false, signedIn: false });
      setHealthLoading(false);
      return false;
    }

    if (healthRefreshInFlight.current) {
      return healthRefreshInFlight.current;
    }

    const refresh = (async () => {
      setHealthLoading(true);
      try {
        const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
        if (sessionError) throw sessionError;

        const session = sessionData.session;
        if (!session) {
          setSignedInEmail(null);
          setHealth({ configured: true, connected: false, signedIn: false });
          return false;
        }

        // Authentication truth comes from Supabase Auth. Health telemetry must never
        // turn a valid restored session into a false sign-out.
        setSignedInEmail(session.user.email ?? null);
        setHealth((current) => ({
          ...current,
          configured: true,
          connected: true,
          signedIn: true,
          error: undefined,
        }));

        const { data, error } = await supabase.rpc("runyi_dashboard_health");
        if (error) {
          setHealth((current) => ({
            ...current,
            configured: true,
            connected: true,
            signedIn: true,
            error: error.message,
          }));
          return true;
        }

        setHealth({
          configured: true,
          connected: true,
          signedIn: true,
          movieCount: Number(data?.movie_count ?? 0),
          conceptRunCount: Number(data?.concept_run_count ?? 0),
          schemaVersion: data?.schema_version ?? "installed",
          privateAccount: Boolean(data?.private_account),
          role: data?.role === "OWNER_ADMIN" ? "OWNER_ADMIN" : data?.role === "ADMIN" ? "ADMIN" : "MEMBER",
          activeUserCount: data?.active_user_count == null ? undefined : Number(data.active_user_count),
          maximumUsers: Number(data?.maximum_users ?? 500),
        });
        return true;
      } catch (error) {
        setHealth((current) => ({
          ...current,
          configured: true,
          connected: current.signedIn === true,
          signedIn: current.signedIn,
          error: error instanceof Error ? error.message : "Database access is not ready",
        }));
        return false;
      } finally {
        setHealthLoading(false);
      }
    })();

    healthRefreshInFlight.current = refresh;
    try {
      return await refresh;
    } finally {
      if (healthRefreshInFlight.current === refresh) {
        healthRefreshInFlight.current = null;
      }
    }
  }, [supabase]);

  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    if (query.get("runyi_android_auth") !== "1") return;

    setIsAndroidAuthReturn(true);
    const fragment = new URLSearchParams(window.location.hash.replace(/^#/, ""));
    const accessToken = fragment.get("access_token");
    const refreshToken = fragment.get("refresh_token");
    if (accessToken && refreshToken) {
      setAndroidAuthHandoff({
        accessToken,
        refreshToken,
        expiresIn: fragment.get("expires_in") ?? "3600",
      });
      window.history.replaceState({}, document.title, `${window.location.pathname}?runyi_android_auth=1`);
    }
  }, []);

  useEffect(() => {
    if (!isAndroidAuthReturn || androidAuthHandoff || !supabase) return;
    void supabase.auth.getSession().then(({ data }) => {
      const session = data.session;
      if (!session) return;
      setAndroidAuthHandoff({
        accessToken: session.access_token,
        refreshToken: session.refresh_token,
        expiresIn: String(session.expires_in ?? 3600),
      });
    });
  }, [androidAuthHandoff, isAndroidAuthReturn, supabase]);

  useEffect(() => {
    if (!supabase) return;

    const acceptSession = async (pending: PendingAndroidSession | undefined) => {
      if (!pending?.accessToken || !pending.refreshToken || androidAuthInProgress.current) return;
      androidAuthInProgress.current = true;
      setAuthLoading(true);
      setAuthNotice("Completing secure sign-in…");

      const { data, error } = await supabase.auth.setSession({
        access_token: pending.accessToken,
        refresh_token: pending.refreshToken,
      });

      if (error || !data.session) {
        window.localStorage.removeItem(PENDING_ANDROID_SESSION_STORAGE);
        setAuthNotice(error?.message ?? "The returned session could not be verified. Request one new sign-in email.");
      } else {
        const runyiWindow = window as Window & { __RUNYI_PENDING_AUTH__?: PendingAndroidSession };
        delete runyiWindow.__RUNYI_PENDING_AUTH__;
        window.localStorage.removeItem(PENDING_ANDROID_SESSION_STORAGE);
        setSignedInEmail(data.session.user.email ?? null);
        setAuthNotice("Signed in successfully. The database connection is being verified.");
        setTab("governance");
        window.history.replaceState({}, document.title, window.location.pathname);
        await refreshHealth();
      }

      setAuthLoading(false);
      androidAuthInProgress.current = false;
    };

    const listener = (event: Event) => {
      void acceptSession((event as CustomEvent<PendingAndroidSession>).detail);
    };
    window.addEventListener("runyi:android-auth", listener);

    const runyiWindow = window as Window & { __RUNYI_PENDING_AUTH__?: PendingAndroidSession };
    let pending = runyiWindow.__RUNYI_PENDING_AUTH__;
    if (!pending) {
      try {
        const stored = window.localStorage.getItem(PENDING_ANDROID_SESSION_STORAGE);
        pending = stored ? JSON.parse(stored) as PendingAndroidSession : undefined;
      } catch {
        window.localStorage.removeItem(PENDING_ANDROID_SESSION_STORAGE);
      }
    }
    void acceptSession(pending);

    return () => window.removeEventListener("runyi:android-auth", listener);
  }, [refreshHealth, supabase]);

  useEffect(() => {
    if (!supabase) {
      const timer = window.setTimeout(() => void refreshHealth(), 0);
      return () => window.clearTimeout(timer);
    }

    let active = true;
    void supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      setSignedInEmail(data.session?.user.email ?? null);
      void refreshHealth();
    });
    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      setSignedInEmail(session?.user.email ?? null);
      window.setTimeout(() => void refreshHealth(), 0);
    });
    return () => {
      active = false;
      listener.subscription.unsubscribe();
    };
  }, [supabase, refreshHealth]);

  const refreshMemberProjectProfile = useCallback(async () => {
    if (!supabase || !signedInEmail) {
      setMemberProjectProfile(null);
      return;
    }
    const { data, error } = await supabase.rpc("runyi_member_project_profile");
    if (error) return;
    setMemberProjectProfile((data ?? { configured: false }) as MemberProjectRuntimeProfile);
  }, [signedInEmail, supabase]);

  useEffect(() => {
    void refreshMemberProjectProfile();
    const listener = () => void refreshMemberProjectProfile();
    window.addEventListener("runyi:member-project-profile-changed", listener);
    return () => window.removeEventListener("runyi:member-project-profile-changed", listener);
  }, [refreshMemberProjectProfile]);

  // One centrally owned refresh callback is consumed by the permanent EnterpriseHeader.
  // Its shared value-sync feed handles change events, foreground, reconnect and bounded
  // reconciliation; do not independently poll this same OWNER ROOT state in the root.
  const refreshAll = useCallback(async () => {
    const [healthy] = await Promise.all([refreshHealth(), refreshMemberProjectProfile()]);
    return healthy !== false;
  }, [refreshHealth, refreshMemberProjectProfile]);

  useEffect(() => {
    if (!memberProjectProfile?.project_ref) return;
    window.localStorage.removeItem(`runyi:member-auth:${memberProjectProfile.project_ref}`);
  }, [memberProjectProfile?.project_ref]);

  useEffect(() => {
    if (signedInEmail) return;
    setTenantSignedInEmail(null);
    setTenantAccessToken(null);
  }, [signedInEmail]);

  async function sendEmailCode(email: string) {
    setAuthNotice(null);
    if (!supabase) {
      setAuthNotice("The embedded Supabase connection is unavailable.");
      return;
    }
    if (!email.trim() || !email.includes("@")) {
      setAuthNotice("Enter the email address you added to the engine allowlist.");
      return;
    }
    setAuthLoading(true);
    const androidBridge = (window as Window & { RunyiAndroid?: unknown }).RunyiAndroid;
    const returnUrl = androidBridge
      ? new URL("https://runyi-movie-creative-engine.pages.dev/mobile-launcher")
      : new URL(window.location.origin);
    returnUrl.searchParams.set("runyi_android_auth", "1");
    const { error } = await supabase.auth.signInWithOtp({
      email: email.trim(),
      options: { emailRedirectTo: returnUrl.toString() },
    });
    setAuthLoading(false);
    setAuthNotice(error ? error.message : "Email sent. Enter the verification code below. The link in the email remains available as a backup.");
  }

  async function verifyEmailCode(email: string, token: string) {
    setAuthNotice(null);
    if (!supabase) {
      setAuthNotice("The embedded Supabase connection is unavailable.");
      return;
    }
    const cleanEmail = email.trim();
    const cleanToken = token.replace(/\D/g, "");
    if (!cleanEmail || !cleanEmail.includes("@")) {
      setAuthNotice("Enter the same allowed email address that received the code.");
      return;
    }
    if (cleanToken.length < 6 || cleanToken.length > 8) {
      setAuthNotice("Enter the complete 6- to 8-digit code from your email.");
      return;
    }

    setAuthLoading(true);
    const { data, error } = await supabase.auth.verifyOtp({
      email: cleanEmail,
      token: cleanToken,
      type: "email",
    });

    if (error || !data.session) {
      setAuthNotice(error?.message ?? "The code could not be verified. Request one new code and try again.");
    } else {
      setSignedInEmail(data.session.user.email ?? cleanEmail);
      setAuthNotice("Code verified. You are signed in and the database connection is being checked.");
      await refreshHealth();
    }
    setAuthLoading(false);
  }

  function returnToAndroidApp() {
    if (!androidAuthHandoff) return;
    const query = new URLSearchParams({
      access_token: androidAuthHandoff.accessToken,
      refresh_token: androidAuthHandoff.refreshToken,
      expires_in: androidAuthHandoff.expiresIn,
    });
    setAndroidReturnNotice("Opening the RUNYI app… If Android asks, choose Open.");
    window.location.assign(
      `intent://auth?${query.toString()}#Intent;scheme=runyi;package=films.runyiawara.movieengine;end`,
    );
  }

  async function signOut() {
    if (!supabase) return;
    setAuthLoading(true);
    await supabase.auth.signOut();
    setTenantSignedInEmail(null);
    setTenantAccessToken(null);
    setAuthLoading(false);
    setAuthNotice("Signed out of the database.");
  }

  useEffect(() => {
    const eventName = "runyi:stage-intake";
    const listener = (event: Event) => {
      const detail = (event as CustomEvent<Partial<Intake>>).detail;
      setIntake((current) => {
        const next = { ...current, ...detail };
        return next.productionProfile === "HUNDRED_SCENE" ? { ...next, duration: "100", pacing: "C" } : next;
      });
      setTab("movies");
      setMovieTab("create");
    };
    window.addEventListener(eventName, listener);

    const context = (document as Document & {
      modelContext?: {
        registerTool?: (tool: unknown) => void;
        unregisterTool?: (name: string) => void;
      };
    }).modelContext;

    context?.registerTool?.({
      name: "read_runyi_engine_status",
      description: "Read the RUNYI creative engine connection and catalogue status.",
      inputSchema: { type: "object", properties: {} },
      execute: async () => ({ content: [{ type: "text", text: JSON.stringify(health) }] }),
    });
    context?.registerTool?.({
      name: "stage_runyi_movie_intake",
      description: "Stage a movie brief in the 12-to-3-to-1 funnel for human review.",
      inputSchema: {
        type: "object",
        properties: {
          theme: { type: "string" },
          storyStatus: { type: "string", enum: ["STANDALONE", "SEQUEL", "FRANCHISE", "ADAPTATION"] },
          platform: { type: "string" },
          aspectRatio: { type: "string" },
          productionProfile: { type: "string", enum: ["STANDARD", "HUNDRED_SCENE"] },
          pacing: { type: "string", enum: ["A", "B", "C"] },
          duration: { type: "string" },
          route: { type: "string", enum: ["O", "T"] },
          sourceForm: { type: "string", enum: ["SOURCE_FREE", "COMPLETE_TRANSCRIPT", "SUPPLIED_SUMMARY"] },
          transformationDistance: { type: "string", enum: ["NONE", "CLOSE", "LOOSE"] },
          primaryThemeLane: { type: "string", enum: ["O1", "O2", "O3", "O4"] },
          secondaryThemeLane: { type: "string", enum: ["NONE", "O1", "O2", "O3", "O4"] },
          emotionalGenre: { type: "string" },
          pressureArena: { type: "string" },
          humanQuestion: { type: "string" },
        },
        required: ["theme"],
      },
      execute: async (args: Partial<Intake>) => {
        window.dispatchEvent(new CustomEvent(eventName, { detail: args }));
        return { content: [{ type: "text", text: "Brief staged for human review; nothing was submitted." }] };
      },
    });

    return () => {
      window.removeEventListener(eventName, listener);
      context?.unregisterTool?.("read_runyi_engine_status");
      context?.unregisterTool?.("stage_runyi_movie_intake");
    };
  }, [health]);

  async function submitRun() {
    setNotice(null);
    setCreatedRun(null);
    if (themeLength < 12) {
      setNotice("Add a clearer creative direction—at least 12 characters.");
      return;
    }
    const duration = Number(intake.duration);
    if (!Number.isFinite(duration) || duration < 1 || duration > 300) {
      setNotice("Choose a target duration between 1 and 300 minutes.");
      return;
    }
    if (intake.productionProfile === "HUNDRED_SCENE" && (duration !== 100 || intake.pacing !== "C")) {
      setNotice("The isolated 100-scene profile requires 100 minutes and pacing C.");
      return;
    }
    if (intake.route === "O" && intake.secondaryThemeLane === intake.primaryThemeLane) {
      setNotice("Choose a different secondary theme lane, or select none.");
      return;
    }
    if (intake.route === "T" && !["COMPLETE_TRANSCRIPT", "SUPPLIED_SUMMARY"].includes(intake.sourceForm)) {
      setNotice("Route T requires a complete transcript or supplied summary.");
      return;
    }
    if (!dataConnected) {
      setNotice(memberDataPlaneInstalled
        ? "Your member data project is installed. Finish the member-project email sign-in in Movies Engine Settings before creating movie data."
        : "Your brief is ready. Finish the RUNYI database sign-in steps in Movies Engine Settings.");
      setTab("movies");
      setMovieTab("settings");
      return;
    }

    setSubmitting(true);
    try {
      if (!dataSupabase) throw new Error("The active RUNYI data connection is unavailable.");
      const canonicalRequest = {
        p_requested_theme: intake.theme.trim(),
        p_platform: intake.platform,
        p_aspect_ratio: intake.aspectRatio,
        p_pacing_option: intake.pacing,
        p_target_duration: duration,
        p_story_status: intake.storyStatus,
        p_production_profile: intake.productionProfile,
      };
      const workflowRequest = {
        ...canonicalRequest,
        p_source_route: intake.route,
        p_source_form: intake.route === "T" ? intake.sourceForm : "SOURCE_FREE",
        p_transformation_distance: intake.route === "T" ? intake.transformationDistance : "NONE",
        p_primary_theme_lane: intake.route === "O" ? intake.primaryThemeLane : "NONE",
        p_secondary_theme_lane: intake.route === "O" ? intake.secondaryThemeLane : "NONE",
        p_emotional_genre: intake.emotionalGenre,
        p_pressure_arena: intake.pressureArena,
        p_human_question: intake.humanQuestion.trim(),
      };
      let { data, error } = await dataSupabase.rpc("runyi_start_concept_run_v3", workflowRequest);

      const canonicalFunctionMissing = Boolean(
        error && (error.code === "PGRST202" || /runyi_start_concept_run_v3|schema cache/i.test(error.message)),
      );
      if (canonicalFunctionMissing && intake.productionProfile === "STANDARD" && intake.route === "O") {
        let fallback = await dataSupabase.rpc("runyi_start_concept_run_v2", canonicalRequest);
        if (fallback.error && (fallback.error.code === "PGRST202" || /runyi_start_concept_run_v2|schema cache/i.test(fallback.error.message))) {
          fallback = await dataSupabase.rpc("runyi_start_concept_run", {
            p_requested_theme: canonicalRequest.p_requested_theme,
            p_platform: canonicalRequest.p_platform,
            p_aspect_ratio: canonicalRequest.p_aspect_ratio,
            p_pacing_option: canonicalRequest.p_pacing_option,
            p_target_duration: canonicalRequest.p_target_duration,
            p_story_status: canonicalRequest.p_story_status,
          });
        }
        data = fallback.data;
        error = fallback.error;
      } else if (canonicalFunctionMissing) {
        throw new Error("Install the 24 + 36 workflow database update before starting this route or production profile.");
      }
      if (error) throw error;
      setCreatedRun(String(data?.id ?? "created"));
      setNotice(canonicalFunctionMissing
        ? "Temporary concept run created in compatibility mode. Install the 24 + 36 database update to preserve the new route and theme fields. No RAF number has been assigned."
        : "Temporary concept run created at canonical Step 1 / supporting Step 1. The 12 → 3 → 1 funnel is ready; no RAF number has been assigned.");
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "Could not start the run");
    } finally {
      setSubmitting(false);
    }
  }

  if (isAndroidAuthReturn) {
    return (
      <AndroidAuthReturn
        ready={Boolean(androidAuthHandoff)}
        notice={androidReturnNotice}
        onReturn={returnToAndroidApp}
      />
    );
  }

  const activeWorkspace = enterpriseWorkspace(tab);
  const immersiveWorkspace = activeWorkspace.viewportMode === "IMMERSIVE";

  return (
    <main data-runyi-workspace={activeWorkspace.route} data-runyi-workspace-scope={activeWorkspace.searchScope}
      className={
        immersiveWorkspace
          ? "runyi-enterprise-screen h-dvh overflow-hidden bg-background text-foreground"
          : "runyi-enterprise-screen min-h-dvh bg-background pb-[calc(var(--runyi-ui-bottom-nav-height,0px)+1.5rem)] text-foreground lg:pb-8"
      }
    >
      <RunyiUiRuntimeCoordinator supabase={supabase} connected={health.connected} screenKey={runyiScreenKeyForRoute(tab)} />
      <EnterpriseHeader
        activeRoute={tab}
        health={health}
        loading={healthLoading}
        onNavigate={setTab}
        onRefresh={refreshAll}
        supabase={supabase}
      />

      <div
        className={
          immersiveWorkspace
            ? "mx-auto grid h-[var(--runyi-ui-available-height,100dvh)] max-w-7xl gap-0 overflow-hidden lg:grid-cols-[220px_minmax(0,1fr)] lg:gap-6 lg:px-6 lg:py-4"
            : "mx-auto grid w-full min-w-0 max-w-7xl gap-3 px-2 py-3 sm:px-6 lg:grid-cols-[220px_minmax(0,1fr)] lg:gap-6 lg:py-7"
        }
      >
        <EnterpriseSidebar activeRoute={tab} onNavigate={setTab} supabase={supabase} connected={health.connected} />

        <section className={immersiveWorkspace ? "min-h-0 min-w-0 w-full overflow-hidden" : "min-w-0 w-full max-w-full"}>
          {tab === "home" && <EnterpriseHome health={health} onNavigate={setTab} />}

          {tab === "ai" && (
            <AiWorkspace
              supabase={supabase}
              connected={health.connected}
              isAdmin={health.role === "OWNER_ADMIN" || health.role === "ADMIN"}
            />
          )}

          {tab === "files" && (
            <UniversalFileExplorer
              supabase={supabase}
              connected={health.connected}
              isAdmin={health.role === "OWNER_ADMIN" || health.role === "ADMIN"}
              role={health.role}
              onNavigate={(route) => setTab(route)}
            />
          )}

          {tab === "applications" && <EnterpriseInfrastructureWorkspace supabase={supabase} connected={health.connected} mode="applications" />}
          {tab === "infrastructure" && <EnterpriseInfrastructureWorkspace supabase={supabase} connected={health.connected} mode="infrastructure" />}
          {tab === "operations" && <EnterpriseInfrastructureWorkspace supabase={supabase} connected={health.connected} mode="flow" />}
          {tab === "more" && <EnterpriseMoreWorkspace onNavigate={setTab} />}

          {tab === "new" && <EnterpriseInfrastructureWorkspace supabase={supabase} connected={health.connected} mode="new" />}
          {tab === "catalogue" && <EnterpriseInfrastructureWorkspace supabase={supabase} connected={health.connected} mode="catalogue" />}
          {tab === "bridge" && <EnterpriseInfrastructureWorkspace supabase={supabase} connected={health.connected} mode="bridge" />}
          {tab === "flow" && <EnterpriseInfrastructureWorkspace supabase={supabase} connected={health.connected} mode="flow" />}

          {tab === "movies" && (
            <div className="space-y-4">
              <div className="rounded-[24px] border border-foreground/8 bg-card p-3 shadow-sm sm:p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary">Application workspace</p>
                    <h1 className="mt-1 text-2xl font-semibold tracking-tight">Movies Engine</h1>
                    <p className="mt-1 text-xs leading-5 text-foreground/45">
                      Preserved Movie Creative Engine workspace. This dashboard is application-scoped and does not inherit RUNYI Enterprise Control Tower authority.
                    </p>
                  </div>
                  <Badge className="bg-primary/[0.08] text-primary">APPLICATION SCOPED</Badge>
                </div>
                <div className="mt-3 flex gap-1 overflow-x-auto pb-1">
                  {movieNavItems.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => setMovieTab(item.id)}
                      className={`flex min-w-max items-center gap-2 rounded-xl px-3 py-2 text-xs font-medium transition ${movieTab === item.id ? "bg-foreground/10 text-foreground shadow-sm" : "text-foreground/45 hover:bg-foreground/[0.05]"}`}
                    >
                      <item.icon className="size-3.5" />{item.label}
                    </button>
                  ))}
                </div>
              </div>

              {movieTab === "ai" && (
                <div className="min-h-[620px] overflow-hidden rounded-[24px]">
                  <AiWorkspace
                    supabase={supabase}
                    connected={health.connected}
                    isAdmin={health.role === "OWNER_ADMIN" || health.role === "ADMIN"}
                  />
                </div>
              )}

              {movieTab === "create" && (
                <CreatePanel
                  health={{ ...health, connected: dataConnected, schemaVersion: memberDataPlaneInstalled ? memberProjectProfile?.schema_version ?? health.schemaVersion : health.schemaVersion }}
                  intake={intake}
                  setIntake={setIntake}
                  readiness={readiness}
                  themeLength={themeLength}
                  notice={notice}
                  createdRun={createdRun}
                  submitting={submitting}
                  supabase={dataSupabase}
                  onSubmit={submitRun}
                />
              )}

              {movieTab === "catalogue" && <LibraryWorkspace onCreate={() => setMovieTab("create")} />}
              {movieTab === "bridge" && <DatabaseBridge supabase={dataSupabase} connected={dataConnected} currentRunId={createdRun} />}
              {movieTab === "workflow" && <WorkflowPanel />}
              {movieTab === "backend" && (
                <div className="min-h-[620px] overflow-hidden">
                  <BackendExplorer
                    supabase={supabase}
                    connected={health.connected}
                    isAdmin={health.role === "OWNER_ADMIN" || health.role === "ADMIN"}
                  />
                </div>
              )}
              {movieTab === "settings" && (
                <SettingsPanel
                  health={health}
                  supabase={supabase}
                  loading={healthLoading}
                  authLoading={authLoading}
                  signedInEmail={signedInEmail}
                  notice={authNotice}
                  appearance={appearance}
                  onAppearanceChange={applyAppearance}
                  onSendCode={sendEmailCode}
                  onVerifyCode={verifyEmailCode}
                  onSignOut={signOut}
                  onRefresh={refreshHealth}
                  memberProjectProfile={memberProjectProfile}
                  memberSupabase={memberSupabase}
                  memberDataSupabase={memberDataSupabase}
                  tenantSignedInEmail={tenantSignedInEmail}
                  onTenantAuthChanged={setTenantSignedInEmail}
                  onTenantAccessTokenChanged={setTenantAccessToken}
                  onRefreshMemberProjectProfile={refreshMemberProjectProfile}
                />
              )}
            </div>
          )}

          {tab === "governance" && (
            <SettingsPanel
              health={health}
              supabase={supabase}
              loading={healthLoading}
              authLoading={authLoading}
              signedInEmail={signedInEmail}
              notice={authNotice}
              appearance={appearance}
              onAppearanceChange={applyAppearance}
              onSendCode={sendEmailCode}
              onVerifyCode={verifyEmailCode}
              onSignOut={signOut}
              onRefresh={refreshHealth}
              memberProjectProfile={memberProjectProfile}
              memberSupabase={memberSupabase}
              memberDataSupabase={memberDataSupabase}
              tenantSignedInEmail={tenantSignedInEmail}
              onTenantAuthChanged={setTenantSignedInEmail}
              onTenantAccessTokenChanged={setTenantAccessToken}
              onRefreshMemberProjectProfile={refreshMemberProjectProfile}
            />
          )}
        </section>
      </div>

      <EnterpriseBottomNav activeRoute={tab} onNavigate={setTab} supabase={supabase} connected={health.connected} />
    </main>
  );
}

function AndroidAuthReturn({ ready, notice, onReturn }: { ready: boolean; notice: string | null; onReturn: () => void }) {
  return (
    <main className="grid min-h-dvh place-items-center bg-background px-5 py-10 text-foreground">
      <Card className="w-full max-w-md border-foreground/10 bg-card py-0">
        <CardContent className="p-6 sm:p-8">
          <div className="grid size-14 place-items-center rounded-2xl border border-primary/30 bg-primary/10 text-primary">
            <Film className="size-7" />
          </div>
          <p className="mt-6 text-xs font-bold uppercase tracking-[0.2em] text-primary">RUNYI ENTERPRISE</p>
          <h1 className="mt-2 text-3xl font-semibold tracking-tight">Return to RUNYI ENTERPRISE</h1>
          <p className="mt-3 text-sm leading-6 text-foreground/55">
            {ready
              ? "Your email was verified. Finish sign-in inside the installed RUNYI app."
              : "Finishing the secure email verification…"}
          </p>
          <Button onClick={onReturn} disabled={!ready} className="mt-7 h-12 w-full bg-primary font-bold text-primary-foreground hover:bg-primary/90">
            {ready ? <LogIn /> : <LoaderCircle className="animate-spin" />}
            {ready ? "Return to RUNYI app" : "Preparing secure return"}
          </Button>
          {notice && <p className="mt-4 text-sm leading-6 text-emerald-200">{notice}</p>}
          <p className="mt-5 text-xs leading-5 text-foreground/35">Requires the current Runyi Android app.</p>
        </CardContent>
      </Card>
    </main>
  );
}

function CreatePanel({ health, intake, setIntake, readiness, themeLength, notice, createdRun, submitting, supabase, onSubmit }: {
  health: Health;
  intake: Intake;
  setIntake: (value: Intake) => void;
  readiness: number;
  themeLength: number;
  notice: string | null;
  createdRun: string | null;
  submitting: boolean;
  supabase: SupabaseClient | null;
  onSubmit: () => void;
}) {
  const duration = Number(intake.duration);
  const sceneEstimate = Number.isFinite(duration)
    ? intake.productionProfile === "HUNDRED_SCENE"
      ? "100 scenes · 20 parts · 1,200 shots"
      : intake.pacing === "B"
        ? `${Number.isInteger((duration * 60) / 45) ? (duration * 60) / 45 : ((duration * 60) / 45).toFixed(1)} scenes at 45 seconds${Number.isInteger((duration * 60) / 45) ? "" : " · partial final scene needs approval"}`
        : `${duration} one-minute scenes`
    : "Choose a duration to calculate the scene plan";
  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="mb-2 text-xs font-bold uppercase tracking-[0.18em] text-primary">Production command room</p>
          <h1 className="max-w-2xl text-3xl font-semibold leading-tight tracking-[-0.035em] sm:text-4xl">Build a story viewers can’t swap for another.</h1>
          <p className="mt-3 max-w-2xl text-sm leading-6 text-foreground/55 sm:text-base">Start with a precise creative direction. The engine records a 12 → 3 → 1 concept funnel, then protects originality, narrative satisfaction, and viewer value.</p>
        </div>
        <div className="grid grid-cols-3 gap-2 sm:w-[350px]">
          <Metric value={String(health.movieCount ?? 0)} label="Catalogue" />
          <Metric value="22" label="Tables" />
          <Metric value="4" label="Vector indexes" />
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1.45fr)_minmax(300px,.75fr)]">
        <Card className="overflow-hidden border-foreground/10 bg-card py-0 shadow-2xl shadow-black/25">
          <div className="border-b border-foreground/8 px-5 py-5 sm:px-7">
            <div className="flex items-start justify-between gap-4">
              <div><p className="text-lg font-semibold">New movie brief</p><p className="mt-1 text-sm text-foreground/50">One strong brief becomes twelve genuinely different possibilities.</p></div>
              <Badge variant="outline" className="border-foreground/15 text-foreground/60">Stage 01</Badge>
            </div>
          </div>
          <CardContent className="space-y-5 px-5 py-6 sm:px-7">
            <Field label="Creative route">
              <div className="grid gap-2 sm:grid-cols-2">
                {[
                  ["O", "Route O", "Original four-theme engine"],
                  ["T", "Route T", "Transcript transformation"],
                ].map(([value, title, description]) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setIntake({
                      ...intake,
                      route: value,
                      sourceForm: value === "T" ? "COMPLETE_TRANSCRIPT" : "SOURCE_FREE",
                      transformationDistance: value === "T" ? "CLOSE" : "NONE",
                    })}
                    className={`rounded-xl border p-4 text-left transition ${intake.route === value ? "border-primary/70 bg-primary/10" : "border-foreground/10 bg-[var(--surface-inset)] hover:border-foreground/20"}`}
                  >
                    <span className="block text-sm font-semibold">{title}</span>
                    <span className="mt-1 block text-xs leading-5 text-foreground/42">{description}</span>
                  </button>
                ))}
              </div>
            </Field>

            {intake.route === "O" ? (
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Primary theme lane"><EngineSelect value={intake.primaryThemeLane} onValueChange={(value) => setIntake({ ...intake, primaryThemeLane: value, secondaryThemeLane: intake.secondaryThemeLane === value ? "NONE" : intake.secondaryThemeLane })} options={themeLanes} /></Field>
                <Field label="Secondary lane · optional"><EngineSelect value={intake.secondaryThemeLane} onValueChange={(value) => setIntake({ ...intake, secondaryThemeLane: value })} options={[["NONE", "None · use one primary lane"], ...themeLanes.filter(([value]) => value !== intake.primaryThemeLane)]} /></Field>
              </div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Source form"><EngineSelect value={intake.sourceForm} onValueChange={(value) => setIntake({ ...intake, sourceForm: value })} options={[["COMPLETE_TRANSCRIPT", "Complete transcript"], ["SUPPLIED_SUMMARY", "Supplied summary"]]} /></Field>
                <Field label="Transformation distance"><EngineSelect value={intake.transformationDistance} onValueChange={(value) => setIntake({ ...intake, transformationDistance: value })} options={[["CLOSE", "Close transformation"], ["LOOSE", "Loose transformation"]]} /></Field>
              </div>
            )}

            <Field label="Creative direction" hint={`${themeLength}/1,200`}>
              <Textarea
                value={intake.theme}
                onChange={(event) => setIntake({ ...intake, theme: event.target.value.slice(0, 1200) })}
                placeholder="Example: A principled midwife must protect the wealthy family that ruined hers—without becoming the kind of person she despises."
                className="min-h-32 border-foreground/12 bg-[var(--surface-inset)] text-[16px] leading-6 text-foreground placeholder:text-foreground/25 focus-visible:border-primary/70 focus-visible:ring-primary/15"
              />
            </Field>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Primary emotional genre"><EngineSelect value={intake.emotionalGenre} onValueChange={(value) => setIntake({ ...intake, emotionalGenre: value })} options={emotionalGenres} /></Field>
              <Field label="Supporting pressure arena"><EngineSelect value={intake.pressureArena} onValueChange={(value) => setIntake({ ...intake, pressureArena: value })} options={pressureArenas} /></Field>
              <Field label="Story type"><EngineSelect value={intake.storyStatus} onValueChange={(value) => setIntake({ ...intake, storyStatus: value })} options={[["STANDALONE", "Standalone"], ["SEQUEL", "Sequel"], ["FRANCHISE", "Franchise"], ["ADAPTATION", "Adaptation"]]} /></Field>
              <Field label="Platform"><EngineSelect value={intake.platform} onValueChange={(value) => setIntake({ ...intake, platform: value })} options={[["YouTube", "YouTube"], ["Facebook", "Facebook"], ["Both", "YouTube + Facebook"]]} /></Field>
              <Field label="Aspect ratio"><EngineSelect value={intake.aspectRatio} onValueChange={(value) => setIntake({ ...intake, aspectRatio: value })} options={[["16:9", "16:9 · Landscape"], ["9:16", "9:16 · Vertical"], ["1:1", "1:1 · Square"]]} /></Field>
              <Field label="Target duration">
                <div className="relative"><Input type="number" min="1" max="300" disabled={intake.productionProfile === "HUNDRED_SCENE"} value={intake.duration} onChange={(event) => setIntake({ ...intake, duration: event.target.value })} className="h-12 border-foreground/12 bg-[var(--surface-inset)] pr-20 text-[16px] text-foreground focus-visible:border-primary/70 focus-visible:ring-primary/15 disabled:opacity-60" /><span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-foreground/35">minutes</span></div>
              </Field>
            </div>

            <Field label="Central human question · optional" hint={`${intake.humanQuestion.length}/240`}>
              <Input value={intake.humanQuestion} onChange={(event) => setIntake({ ...intake, humanQuestion: event.target.value.slice(0, 240) })} placeholder="What must this story force the audience to feel or decide?" className="h-12 border-foreground/12 bg-[var(--surface-inset)] text-[16px] text-foreground placeholder:text-foreground/25 focus-visible:border-primary/70 focus-visible:ring-primary/15" />
            </Field>

            <Field label="Production profile">
              <EngineSelect
                value={intake.productionProfile}
                onValueChange={(value) => setIntake({
                  ...intake,
                  productionProfile: value,
                  duration: value === "HUNDRED_SCENE" ? "100" : intake.duration,
                  pacing: value === "HUNDRED_SCENE" ? "C" : intake.pacing,
                })}
                options={[["STANDARD", "Standard movie · flexible duration"], ["HUNDRED_SCENE", "100-scene special · fixed 100 minutes"]]}
              />
              <p className="mt-2 text-xs leading-5 text-foreground/38">The 100-scene structure is isolated from ordinary movie pacing and activates only when selected.</p>
            </Field>

            <Field label="Scene pacing">
              <div className="grid grid-cols-3 gap-2">
                {[["A", "Measured", "60 sec · 4×15"], ["B", "Balanced", "45 sec · 3×15"], ["C", "Intensive", "60 sec · 6×10"]].map(([value, title, meta]) => (
                  <button key={value} type="button" disabled={intake.productionProfile === "HUNDRED_SCENE" && value !== "C"} onClick={() => setIntake({ ...intake, pacing: value })} className={`min-h-[80px] rounded-xl border px-2 py-3 text-left transition disabled:cursor-not-allowed disabled:opacity-30 ${intake.pacing === value ? "border-primary/70 bg-primary/10" : "border-foreground/10 bg-[var(--surface-inset)] hover:border-foreground/20"}`}>
                    <span className="block text-sm font-semibold">{value} · {title}</span><span className="mt-1 block text-[11px] leading-4 text-foreground/40">{meta}</span>
                  </button>
                ))}
              </div>
              <p className="mt-2 text-xs leading-5 text-foreground/38">{sceneEstimate}. Every beat divides into approved five-second shots.</p>
            </Field>

            <div className="rounded-xl border border-foreground/8 bg-[var(--surface-inset)] p-4">
              <div className="mb-2 flex items-center justify-between text-xs"><span className="font-medium text-foreground/65">Brief readiness</span><span className="font-semibold text-primary">{readiness}%</span></div>
              <Progress value={readiness} className="bg-foreground/8 [&_[data-slot=progress-indicator]]:bg-primary" />
            </div>

            {notice && (
              <div className="flex gap-3 rounded-xl border border-amber-300/20 bg-amber-300/8 p-4 text-sm leading-5 text-amber-100">
                <CircleAlert className="mt-0.5 size-4 shrink-0 text-amber-300" />
                <span>{notice}{createdRun && <span className="mt-1 block font-mono text-xs text-amber-200/60">Run {createdRun}</span>}</span>
              </div>
            )}

            <Button onClick={onSubmit} disabled={submitting} size="lg" className="h-13 w-full rounded-xl bg-primary text-[15px] font-bold text-primary-foreground shadow-lg shadow-primary/10 hover:bg-primary/90">
              {submitting ? <LoaderCircle className="animate-spin" /> : <Sparkles />}
              Start 12 → 3 → 1 creative run
              {!submitting && <ChevronRight className="ml-auto" />}
            </Button>
            <p className="text-center text-xs leading-5 text-foreground/35">This creates a temporary Concept Registry entry. Only the approved winner enters the permanent Movie Registry and receives an RAF number.</p>
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card className="border-foreground/10 bg-card py-0">
            <CardContent className="p-5">
              <div className="mb-5 flex items-center gap-3"><div className="grid size-10 place-items-center rounded-xl bg-emerald-400/10 text-emerald-300"><ShieldCheck className="size-5" /></div><div><p className="font-semibold">Creative-value gate</p><p className="text-xs text-foreground/40">Required before GREEN</p></div></div>
              <ul className="space-y-3">{creativeChecks.map((check) => <li key={check} className="flex gap-3 text-sm leading-5 text-foreground/62"><span className="mt-0.5 grid size-5 shrink-0 place-items-center rounded-full border border-emerald-400/25 bg-emerald-400/8"><Check className="size-3 text-emerald-300" /></span>{check}</li>)}</ul>
            </CardContent>
          </Card>
          <Card className="border-foreground/10 bg-gradient-to-br from-card to-background py-0">
            <CardContent className="p-5">
              <div className="flex items-center gap-3"><Waypoints className="size-5 text-primary" /><p className="font-semibold">Three-stage concept funnel</p></div>
              <div className="mt-5 grid grid-cols-[1fr_auto_1fr_auto_1fr] items-center gap-2 text-center"><FunnelStep value="12" label="Loglines" active /><ChevronRight className="size-4 text-foreground/20" /><FunnelStep value="3" label="Blueprints" /><ChevronRight className="size-4 text-foreground/20" /><FunnelStep value="1" label="Winner" /></div>
              <p className="mt-5 text-xs leading-5 text-foreground/40">Generate twelve one-sentence concepts, eliminate nine cheaply, then expand only the strongest three.</p>
            </CardContent>
          </Card>
          <Card className="border-foreground/10 bg-card py-0">
            <CardContent className="p-5">
              <div className="flex items-center gap-3"><Search className="size-5 text-primary" /><p className="font-semibold">Originality search scope</p></div>
              <div className="mt-4 space-y-3 text-sm leading-5 text-foreground/58">
                <p><span className="font-semibold text-foreground/78">Latest 10:</span> immediate repetition and audience-fatigue check.</p>
                <p><span className="font-semibold text-foreground/78">Nearest 3–10:</span> semantic matches from the entire approved catalogue.</p>
                <p><span className="font-semibold text-foreground/78">Full history:</span> overused combinations, themes, and ending families.</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {createdRun && supabase && <ManualChatGPTDesk runId={createdRun} intake={intake} supabase={supabase} />}
    </div>
  );
}

function WorkflowPanel() {
  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-primary">Canonical production system</p>
        <h1 className="mt-2 text-3xl font-semibold tracking-tight sm:text-4xl">24 stages, supported by 36 operations</h1>
        <p className="mt-3 max-w-3xl text-sm leading-6 text-foreground/52">The 24 stages remain the authority. Open a phase to see where the expanded operating steps perform the database, originality, audit, approval, and publication work.</p>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <StatusCard label="Canonical authority" value="24 stages" icon={Workflow} />
        <StatusCard label="Operating support" value="36 steps" icon={Waypoints} />
        <StatusCard label="Concept funnel" value="12 → 3 → 1" icon={Sparkles} />
      </div>

      <Card className="border-amber-300/18 bg-amber-300/[0.055] py-0">
        <CardContent className="p-5 text-sm leading-6 text-amber-50/78">
          <p className="font-semibold text-primary">RAF boundary</p>
          <p className="mt-1">Supporting Step 22 records promotion eligibility only. The permanent RAF number is assigned during supporting Step 34, exactly when the approved blueprint enters canonical Stage 22.</p>
        </CardContent>
      </Card>

      <Accordion type="single" collapsible className="space-y-3">
        {workflowPhases.map((group, index) => (
          <AccordionItem key={group.phase} value={`phase-${index}`} className="overflow-hidden rounded-2xl border border-foreground/10 bg-card px-5 sm:px-6">
            <AccordionTrigger className="py-5 text-foreground hover:no-underline">
              <span className="flex min-w-0 items-start gap-3">
                <span className="grid size-8 shrink-0 place-items-center rounded-lg border border-primary/25 bg-primary/8 text-xs font-bold text-primary">{index + 1}</span>
                <span><span className="block font-semibold">{group.phase}</span><span className="mt-1 block text-xs font-normal text-foreground/38">{group.canonical.length} canonical {group.canonical.length === 1 ? "stage" : "stages"}</span></span>
              </span>
            </AccordionTrigger>
            <AccordionContent className="pb-5">
              <div className="space-y-3">
                {group.canonical.map(([number, title, ...support]) => (
                  <div key={`${number}-${title}`} className="rounded-xl border border-foreground/8 bg-[var(--surface-inset)] p-4">
                    <div className="flex items-start gap-3">
                      <Badge variant="outline" className="shrink-0 border-foreground/15 text-primary">{number}</Badge>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-foreground/86">{title}</p>
                        <ul className="mt-2 space-y-2">
                          {support.map((step) => (
                            <li key={step} className="flex gap-2 text-xs leading-5 text-foreground/48"><ChevronRight className="mt-1 size-3 shrink-0 text-primary" />{step}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>

      <Card className="border-foreground/10 bg-gradient-to-br from-card to-background py-0">
        <CardContent className="p-5 sm:p-6">
          <div className="flex items-center gap-3"><ShieldCheck className="size-5 text-emerald-300" /><p className="font-semibold">Registry rule</p></div>
          <p className="mt-3 text-sm leading-6 text-foreground/52">All 12 early concepts live in the temporary Concept Registry under RAFC IDs. Rejections, mutations, and shortlist decisions are preserved there. Only the human-approved winner crosses into the permanent Movie Registry.</p>
        </CardContent>
      </Card>
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return <label className="block"><span className="mb-2 flex items-center justify-between text-sm font-medium text-foreground/72"><span>{label}</span>{hint && <span className="text-xs font-normal text-foreground/30">{hint}</span>}</span>{children}</label>;
}

function EngineSelect({ value, onValueChange, options }: { value: string; onValueChange: (value: string) => void; options: string[][] }) {
  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger className="h-12 w-full border-foreground/12 bg-[var(--surface-inset)] px-3 text-[16px] text-foreground focus-visible:border-primary/70 focus-visible:ring-primary/15"><SelectValue /></SelectTrigger>
      <SelectContent className="border-foreground/10 bg-popover text-foreground">{options.map(([optionValue, label]) => <SelectItem key={optionValue} value={optionValue} className="py-3 focus:bg-foreground/10 focus:text-foreground">{label}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function Metric({ value, label }: { value: string; label: string }) {
  return <div className="rounded-xl border border-foreground/8 bg-foreground/[0.035] px-3 py-3"><p className="text-lg font-semibold text-foreground">{value}</p><p className="mt-0.5 truncate text-[10px] uppercase tracking-wider text-foreground/35">{label}</p></div>;
}

function FunnelStep({ value, label, active = false }: { value: string; label: string; active?: boolean }) {
  return <div><div className={`mx-auto grid size-11 place-items-center rounded-full border text-lg font-semibold ${active ? "border-primary/60 bg-primary/10 text-primary" : "border-foreground/10 bg-[var(--surface-inset)] text-foreground/55"}`}>{value}</div><p className="mt-2 text-[10px] uppercase tracking-wider text-foreground/35">{label}</p></div>;
}

function LibraryWorkspace({ onCreate }: { onCreate: () => void }) {
  const [view, setView] = useState<"catalogue" | "reviews">("catalogue");
  return (
    <div className="space-y-4">
      <div className="inline-grid grid-cols-2 rounded-xl bg-foreground/[0.06] p-1">
        <button type="button" onClick={() => setView("catalogue")} className={`rounded-lg px-4 py-2 text-sm font-medium ${view === "catalogue" ? "bg-background shadow-sm" : "text-foreground/45"}`}>Catalogue</button>
        <button type="button" onClick={() => setView("reviews")} className={`rounded-lg px-4 py-2 text-sm font-medium ${view === "reviews" ? "bg-background shadow-sm" : "text-foreground/45"}`}>Reviews</button>
      </div>
      {view === "catalogue" ? (
        <EmptyPanel icon={Clapperboard} eyebrow="20,000-movie memory" title="Your originality catalogue" body="Only approved winners enter this permanent registry and receive RAF numbers. Similarity search checks the entire approved catalogue." action="Create first movie" onAction={onCreate} />
      ) : (
        <EmptyPanel icon={BookOpenCheck} eyebrow="Human judgment" title="Creative reviews" body="GREEN, YELLOW, and RED decisions collect here. Early concepts remain temporary; only a human-approved winner can enter the Movie Registry." action="Open new brief" onAction={onCreate} />
      )}
    </div>
  );
}

function EmptyPanel({ icon: Icon, eyebrow, title, body, action, onAction }: { icon: LucideIcon; eyebrow: string; title: string; body: string; action: string; onAction: () => void }) {
  return <div className="grid min-h-[70vh] place-items-center"><div className="max-w-lg text-center"><div className="mx-auto mb-5 grid size-14 place-items-center rounded-2xl border border-primary/25 bg-primary/8 text-primary"><Icon className="size-6" /></div><p className="text-xs font-bold uppercase tracking-[0.18em] text-primary">{eyebrow}</p><h1 className="mt-3 text-3xl font-semibold tracking-tight">{title}</h1><p className="mt-3 text-sm leading-6 text-foreground/50">{body}</p><Button onClick={onAction} className="mt-6 h-11 bg-foreground text-background hover:bg-foreground/90"><Plus />{action}</Button></div></div>;
}
